import { useQuery } from "@tanstack/react-query";
import { Activity } from "lucide-react";
import { Link } from "react-router-dom";
import { apiGet } from "../../lib/http";
import { duration, tokens, traceLabel, tracePath, useNeioStatus, type TraceList, type TraceSummary } from "../../lib/neio";
import { utcTime } from "../admin/format";
import { Empty } from "../States";
import { neioState } from "./TraceView";

/** The traces as links to the trace screen, routing first, then tags in name order. */
export function TraceLinks({ traces }: { traces: TraceSummary[] }) {
  const sorted = [...traces].sort((a, b) => {
    const la = traceLabel(a), lb = traceLabel(b);
    const ra = la.startsWith("Tag ") ? 1 : 0, rb = lb.startsWith("Tag ") ? 1 : 0;
    return ra - rb || la.localeCompare(lb) || String(a.created_at ?? "").localeCompare(String(b.created_at ?? ""));
  });
  return (
    <ul className="stub-list trace-links">
      {sorted.map((t) => {
        const figures = [duration(t.duration_ms), tokens(t.total_tokens), t.node_count ? `${t.node_count} steps` : "", utcTime(t.created_at)]
          .filter(Boolean).join(", ");
        return (
          <li key={t.request_id}>
            <Link to={tracePath(t.request_id)}>{traceLabel(t)}</Link>
            {figures && <span className="muted num" style={{ fontSize: 12 }}> {figures}</span>}
          </li>
        );
      })}
    </ul>
  );
}

/** The section body, without the fetching. */
export function TraceListBody({ loading, error, data, onRetry }: {
  loading: boolean; error: unknown; data: TraceList | undefined; onRetry?: () => void;
}) {
  const state = neioState({
    what: "Reading the NeIO Core traces", loading, status: undefined, error, onRetry,
    notFound: <Empty title="NeIO Core has no traces for this yet" />,
  });
  if (state) return state;
  if (!data || data.traces.length === 0) {
    return <Empty title="No traces recorded yet">A trace is recorded when the work runs and sent once NeIO Core answers.</Empty>;
  }
  return <TraceLinks traces={data.traces} />;
}

/**
 * "NeIO Core traces" for a module run or a UC1 job. Shows nothing at all while NeIO's status is
 * unknown or off, so an environment without NeIO has no NeIO links.
 */
export function NeioTraces({ runId, jobId }: { runId?: string; jobId?: string }) {
  const { enabled } = useNeioStatus();
  const query = useQuery({
    queryKey: ["neio", "traces", runId ? "run" : "job", runId ?? jobId],
    queryFn: () => runId
      ? apiGet("/api/v1/neio/runs/{run_id}/traces", { path: { run_id: runId } })
      : apiGet("/api/v1/neio/uc1/jobs/{job_id}/traces", { path: { job_id: jobId! } }),
    enabled: enabled && !!(runId || jobId),
    retry: false,
  });
  if (!enabled) return null;
  return (
    <section className="card stack" style={{ gap: 8 }} aria-label="NeIO Core traces">
      <h3 style={{ fontSize: 16 }}><span className="row"><Activity size={16} aria-hidden="true" /> NeIO Core traces</span></h3>
      <TraceListBody loading={query.isPending} error={query.error} data={query.data} onRetry={() => void query.refetch()} />
    </section>
  );
}
